import { Component } from '@theme/component';
import { isClickedOutside, normalizeString, onAnimationEnd } from '@theme/utilities';

/**
 * Enhances Shopify's native localization selects with two shared searchable listboxes.
 * The selects and submit button remain available if enhancement cannot start.
 */
class LocalizationFormComponent extends Component {
  /** @type {Array<object>} */
  #fields = [];
  /** @type {object | null} */
  #openField = null;
  /** @type {AbortController | null} */
  #listeners = null;
  /** @type {AbortController | null} */
  #openListeners = null;
  /** @type {MutationObserver | null} */
  #contentObserver = null;
  #initializationFrame = 0;
  /** @type {Map<object, number>} */
  #closeTimers = new Map();
  #consumeEscapeKeyup = false;

  connectedCallback() {
    super.connectedCallback();
    if (this.hasAttribute('data-enhanced')) return;

    this.#initialize();
    this.#initializationFrame = window.requestAnimationFrame(() => this.#initialize());
    if (!this.hasAttribute('data-enhanced')) {
      this.#contentObserver = new MutationObserver(() => this.#initialize());
      this.#contentObserver.observe(this, { childList: true, subtree: true });
    }
  }

  #initialize() {
    if (this.hasAttribute('data-enhanced')) return;
    const roots = [...this.querySelectorAll('.alice-localization-choice')];
    if (!this.querySelector('.localization-form__fallback-submit') || roots.length === 0 || roots.some((root) => !root.querySelector('select') || !root.querySelector('.alice-localization-choice__status'))) return;
    try {
      this.#fields = roots.map((root) => this.#buildField(root));
      if (this.#fields.length === 0 || this.#fields.some((field) => !field)) return;

      this.#listeners = new AbortController();
      const { signal } = this.#listeners;
      this.addEventListener('click', this.#onClick, { signal });
      this.addEventListener('input', this.#onInput, { signal });
      this.addEventListener('keydown', this.#onKeyDown, { signal });
      this.addEventListener('keyup', this.#onKeyUp, { signal });
      this.addEventListener('pointermove', this.#onPointerMove, { signal });

      for (const field of this.#fields) field.enhanced.hidden = false;
      this.setAttribute('data-enhanced', '');
      this.#contentObserver?.disconnect();
      this.#contentObserver = null;
    } catch (error) {
      this.#listeners?.abort();
      this.#fields = [];
      console.warn('Localization enhancement unavailable; native selectors remain available.', error);
    }
  }

  disconnectedCallback() {
    window.cancelAnimationFrame(this.#initializationFrame);
    this.#contentObserver?.disconnect();
    this.#contentObserver = null;
    this.#closeSelector(false, true);
    this.#listeners?.abort();
    for (const timer of this.#closeTimers.values()) window.clearTimeout(timer);
    this.#closeTimers.clear();
    for (const field of this.#fields) {
      if (field.popoverShown) {
        field.panel.hidePopover();
        field.popoverShown = false;
      }
    }
    this.#fields = [];
    this.removeAttribute('data-enhanced');
    super.disconnectedCallback();
  }

  #buildField(root) {
    const native = root.querySelector('select');
    const enhanced = root.querySelector('.alice-localization-choice__enhanced');
    const trigger = root.querySelector('.alice-localization-choice__trigger');
    const value = root.querySelector('.alice-localization-choice__value');
    const panel = root.querySelector('.alice-localization-choice__panel');
    const search = root.querySelector('.alice-localization-choice__search');
    const list = root.querySelector('.alice-localization-choice__list');
    const empty = root.querySelector('.alice-localization-choice__empty');
    const status = root.querySelector('.alice-localization-choice__status');
    if (!native || !enhanced || !trigger || !value || !panel || !search || !list || !empty || !status) return null;

    const rows = [];
    const fragment = document.createDocumentFragment();
    for (const option of native.options) {
      const row = document.createElement('div');
      row.className = 'alice-localization-choice__option';
      row.id = `${list.id}-${option.value}`;
      row.setAttribute('role', 'option');
      row.setAttribute('aria-selected', String(option.selected));
      row.dataset.value = option.value;
      row.dataset.search = normalizeString(option.dataset.name || option.textContent || '');
      row.tabIndex = -1;

      const name = document.createElement('span');
      name.className = 'alice-localization-choice__option-name';
      name.textContent = option.dataset.name || option.textContent?.trim() || '';
      row.append(name);

      if (option.dataset.currency) {
        const currency = document.createElement('span');
        currency.className = 'alice-localization-choice__option-currency';
        currency.textContent = option.dataset.currency;
        row.append(currency);
      }

      const check = document.createElement('span');
      check.className = 'alice-localization-choice__option-check';
      check.setAttribute('aria-hidden', 'true');
      check.textContent = '✓';
      row.append(check);
      fragment.append(row);
      rows.push(row);
    }

    list.replaceChildren(fragment);
    panel.hidden = false;
    panel.inert = true;
    panel.setAttribute('aria-hidden', 'true');
    return { root, native, enhanced, trigger, value, panel, search, list, empty, status, rows, visible: rows, activeIndex: -1, popoverShown: false };
  }

  #fieldFor(target) {
    if (!(target instanceof Element)) return null;
    return this.#fields.find((field) => field.root.contains(target)) || null;
  }

  #onClick = (event) => {
    const target = event.target;
    const field = this.#fieldFor(target);
    if (!field || !(target instanceof Element)) return;

    if (target.closest('.alice-localization-choice__trigger')) {
      this.#openField === field ? this.#closeSelector(true) : this.#openSelector(field);
      return;
    }

    if (target.closest('.alice-localization-choice__switch')) {
      const other = this.#fields.find((candidate) => candidate !== field);
      if (other) {
        this.#openSelector(other);
        window.setTimeout(() => {
          if (this.isConnected && this.#openField === other) other.search.focus();
        }, 180);
      }
      return;
    }

    const option = target.closest('.alice-localization-choice__option');
    if (option) this.#select(field, option.dataset.value);
  };

  #onPointerMove = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.#openField) return;
    const option = target.closest('.alice-localization-choice__option');
    if (!option || !this.#openField.root.contains(option)) return;
    const index = this.#openField.visible.indexOf(option);
    if (index >= 0 && index !== this.#openField.activeIndex) this.#activate(this.#openField, index, false);
  };

  #onInput = (event) => {
    const field = this.#fieldFor(event.target);
    if (field && event.target === field.search) this.#filter(field);
  };

  #onKeyDown = (event) => {
    const target = event.target;
    const field = this.#fieldFor(event.target);
    if (!field) return;

    if (event.target === field.trigger) {
      if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        this.#openSelector(field);
        if (event.key === 'Home') this.#activate(field, 0);
        if (event.key === 'End') this.#activate(field, field.visible.length - 1);
        if (event.key === 'ArrowUp') this.#activate(field, field.visible.length - 1);
      } else if (event.key === 'Escape' && this.#openField === field) {
        event.preventDefault();
        event.stopPropagation();
        this.#consumeEscapeKeyup = true;
        this.#closeSelector(true);
      }
      return;
    }

    if (this.#openField !== field) return;
    if (target instanceof Element && target.closest('.alice-localization-choice__switch')) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.#consumeEscapeKeyup = true;
        this.#closeSelector(true);
      } else if (event.key === 'Tab' && !event.shiftKey) {
        this.#closeSelector(false);
      }
      return;
    }
    if (event.target !== field.search) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#activate(field, Math.min(field.activeIndex + 1, field.visible.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#activate(field, Math.max(field.activeIndex - 1, 0));
        break;
      case 'Home':
        event.preventDefault();
        this.#activate(field, 0);
        break;
      case 'End':
        event.preventDefault();
        this.#activate(field, field.visible.length - 1);
        break;
      case 'Enter':
        event.preventDefault();
        if (field.activeIndex >= 0) this.#select(field, field.visible[field.activeIndex].dataset.value);
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.#consumeEscapeKeyup = true;
        this.#closeSelector(true);
        break;
      case 'Tab':
        if (event.shiftKey) this.#closeSelector(false);
        break;
    }
  };

  #onKeyUp = (event) => {
    if (event.key !== 'Escape' || !this.#consumeEscapeKeyup) return;
    event.stopPropagation();
    this.#consumeEscapeKeyup = false;
  };

  #filter(field) {
    const query = normalizeString(field.search.value.trim());
    field.visible = field.rows.filter((row) => {
      const matches = row.dataset.search.includes(query) || row.dataset.value.toLowerCase() === query;
      row.hidden = !matches;
      return matches;
    });
    field.empty.hidden = field.visible.length > 0;
    field.status.textContent = (this.dataset.resultsTemplate || '[count]').replace('[count]', String(field.visible.length));
    field.list.scrollTop = 0;
    const selectedIndex = field.visible.findIndex((row) => row.dataset.value === field.native.value);
    this.#activate(field, selectedIndex >= 0 ? selectedIndex : 0, false);
  }

  #activate(field, index, scroll = true) {
    for (const row of field.rows) row.removeAttribute('data-active');
    if (index < 0 || index >= field.visible.length) {
      field.activeIndex = -1;
      field.search.removeAttribute('aria-activedescendant');
      return;
    }
    const row = field.visible[index];
    field.activeIndex = index;
    row.setAttribute('data-active', '');
    field.search.setAttribute('aria-activedescendant', row.id);
    if (scroll) {
      const rowRect = row.getBoundingClientRect();
      const listRect = field.list.getBoundingClientRect();
      if (rowRect.top < listRect.top) field.list.scrollTop -= listRect.top - rowRect.top;
      if (rowRect.bottom > listRect.bottom) field.list.scrollTop += rowRect.bottom - listRect.bottom;
    }
  }

  #select(field, code) {
    if (!code || ![...field.native.options].some((option) => option.value === code)) return;
    field.native.value = code;
    const option = field.native.selectedOptions[0];
    field.value.textContent = option?.textContent?.trim() || '';
    for (const row of field.rows) row.setAttribute('aria-selected', String(row.dataset.value === code));
    this.#closeSelector(false, true);
    this.querySelector('form')?.submit();
  }

  #openSelector(field) {
    if (this.#openField && this.#openField !== field) this.#closeSelector(false, true);
    if (this.#openField === field) return;

    const timer = this.#closeTimers.get(field);
    if (timer) window.clearTimeout(timer);
    this.#closeTimers.delete(field);
    this.#openField = field;
    field.search.value = '';
    this.#filter(field);
    field.panel.inert = false;
    field.panel.setAttribute('aria-hidden', 'false');
    if (typeof field.panel.showPopover === 'function' && !field.popoverShown) {
      try {
        field.panel.showPopover();
        field.popoverShown = true;
      } catch {
        field.popoverShown = false;
      }
    }
    this.#positionPanel(field);
    field.panel.setAttribute('data-open', '');
    field.trigger.setAttribute('aria-expanded', 'true');
    this.#openListeners = new AbortController();
    const { signal } = this.#openListeners;
    document.addEventListener('pointerdown', this.#onDocumentPointerDown, { signal });
    window.addEventListener('resize', this.#onViewportChange, { signal });
    window.addEventListener('scroll', this.#onViewportChange, { signal, capture: true });
    window.visualViewport?.addEventListener('resize', this.#onViewportChange, { signal });
    window.visualViewport?.addEventListener('scroll', this.#onViewportChange, { signal });
    field.search.focus();
  }

  #closeSelector(restoreFocus = false, immediate = false) {
    const field = this.#openField;
    if (!field) return;
    this.#openField = null;
    this.#openListeners?.abort();
    this.#openListeners = null;
    field.trigger.setAttribute('aria-expanded', 'false');
    field.panel.removeAttribute('data-open');
    field.panel.inert = true;
    field.panel.setAttribute('aria-hidden', 'true');
    field.search.value = '';
    field.search.removeAttribute('aria-activedescendant');
    if (restoreFocus) field.trigger.focus();

    const hide = () => {
      if (field.popoverShown) {
        field.panel.hidePopover();
        field.popoverShown = false;
      }
      this.#closeTimers.delete(field);
    };
    if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) hide();
    else this.#closeTimers.set(field, window.setTimeout(hide, 160));
  }

  #onDocumentPointerDown = (event) => {
    if (this.#openField && !event.composedPath().includes(this.#openField.root)) this.#closeSelector(false);
  };

  #onViewportChange = () => {
    if (this.#openField) this.#positionPanel(this.#openField);
  };

  #positionPanel(field) {
    const trigger = field.trigger.getBoundingClientRect();
    const viewport = window.visualViewport;
    const topEdge = viewport?.offsetTop || 0;
    const leftEdge = viewport?.offsetLeft || 0;
    const viewportHeight = viewport?.height || window.innerHeight;
    const viewportWidth = viewport?.width || window.innerWidth;
    const bottomEdge = topEdge + viewportHeight;
    const rightEdge = leftEdge + viewportWidth;
    const margin = 12;
    const gap = 7;
    const desiredHeight = Math.min(366, 118 + Math.min(field.rows.length, 6) * 42);
    const below = bottomEdge - trigger.bottom - gap - margin;
    const above = trigger.top - topEdge - gap - margin;
    const placeBelow = below >= desiredHeight || below >= above;
    const available = Math.max(100, placeBelow ? below : above);
    const height = Math.min(desiredHeight, available, viewportHeight - margin * 2);
    const width = Math.min(trigger.width, viewportWidth - margin * 2);
    const left = Math.max(leftEdge + margin, Math.min(trigger.left, rightEdge - margin - width));
    const top = placeBelow ? trigger.bottom + gap : trigger.top - gap - height;

    field.panel.dataset.placement = placeBelow ? 'below' : 'above';
    field.panel.style.left = `${Math.round(left)}px`;
    field.panel.style.top = `${Math.round(Math.max(topEdge + margin, top))}px`;
    field.panel.style.width = `${Math.round(width)}px`;
    field.panel.style.maxHeight = `${Math.round(height)}px`;
    field.list.style.maxHeight = `${Math.max(60, Math.round(height - 118))}px`;
  }

  resetForm() {
    this.#closeSelector(false, true);
    for (const field of this.#fields) field.search.value = '';
  }

  enhance() {
    this.#initialize();
  }

  focusPrimaryTrigger = () => {
    const first = this.#fields[0];
    (first?.trigger || this.querySelector('select'))?.focus();
  };
}

/**
 * The existing Horizon header popup owns outer dismissal and focus restoration.
 * @extends {Component}
 */
class DropdownLocalizationComponent extends Component {
  get isHidden() {
    return this.refs.panel.hasAttribute('hidden');
  }

  toggleSelector() {
    return this.isHidden ? this.showPanel() : this.hidePanel();
  }

  showPanel() {
    if (!this.isHidden) return;
    this.addEventListener('keydown', this.#handleKeyDown);
    document.addEventListener('click', this.#handleClickOutside);
    this.refs.panel.removeAttribute('hidden');
    this.refs.button.setAttribute('aria-expanded', 'true');
    this.refs.localizationForm?.enhance();
    onAnimationEnd(this.refs.panel, () => {
      if (!this.isHidden) this.refs.localizationForm?.focusPrimaryTrigger();
    });
  }

  hidePanel = () => {
    if (this.isHidden) return;
    this.removeEventListener('keydown', this.#handleKeyDown);
    document.removeEventListener('click', this.#handleClickOutside);
    this.refs.localizationForm?.resetForm();
    this.refs.button?.setAttribute('aria-expanded', 'false');
    this.refs.panel.setAttribute('hidden', '');
  };

  disconnectedCallback() {
    this.removeEventListener('keydown', this.#handleKeyDown);
    document.removeEventListener('click', this.#handleClickOutside);
    super.disconnectedCallback();
  }

  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) this.hidePanel();
  };

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    this.hidePanel();
    event.stopPropagation();
    this.refs.button?.focus();
  };
}

/**
 * The existing Horizon mobile drawer owns its submenu navigation.
 * @extends {Component}
 */
class DrawerLocalizationComponent extends Component {
  toggle(event) {
    const { target } = event;
    if (!(target instanceof HTMLDetailsElement)) return;
    if (target.open) {
      this.refs.localizationForm?.enhance();
      onAnimationEnd(target, () => this.refs.localizationForm?.focusPrimaryTrigger());
    }
    else this.refs.localizationForm?.resetForm();
  }
}

if (!customElements.get('localization-form-component')) {
  customElements.define('localization-form-component', LocalizationFormComponent);
}
if (!customElements.get('dropdown-localization-component')) {
  customElements.define('dropdown-localization-component', DropdownLocalizationComponent);
}
if (!customElements.get('drawer-localization-component')) {
  customElements.define('drawer-localization-component', DrawerLocalizationComponent);
}
